import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

interface Coordinates {
  latitude: number | null;
  longitude: number | null;
}

interface GeolocationContextProps {
  isSupported: boolean;
  isEnabled: boolean;
  coordinates: Coordinates;
  error: string | null;
  requestPermission: () => void;
  askForPermissionIfNeeded: () => Promise<boolean>;
}

const GeolocationContext = createContext<GeolocationContextProps | undefined>(undefined);

interface GeolocationProviderProps {
  children: React.ReactNode;
  initiate?: boolean;
}

export const GeolocationProvider: React.FC<GeolocationProviderProps> = ({ children }) => {
  const [isSupported, setIsSupported] = useState(!!navigator.geolocation);
  const [isEnabled, setIsEnabled] = useState(false);
  const [coordinates, setCoordinates] = useState<Coordinates>({ latitude: null, longitude: null });
  const [error, setError] = useState<string | null>(null);
  const watchId = useRef<number | null>(null);

  const startWatching = () => {
    if (!navigator.geolocation) {
      setIsSupported(false);
      setError('Geolocation is not supported by this browser.');
      return;
    }

    if (watchId.current !== null) {
      return;
    }

    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates({ latitude, longitude });
        setIsEnabled(true);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setIsEnabled(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const stopWatching = () => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setIsEnabled(false);
  };

  const handlePermission = (state: PermissionState) => {
    if (state === 'granted') {
      setIsEnabled(true);
      startWatching();
    } else if (state === 'denied') {
      setIsEnabled(false);
      setError('Geolocation permission denied.');
      stopWatching();
    } else {
      setIsEnabled(false);
    }
  };

  const checkPermission = async () => {
    if (!navigator.permissions) {
      setError('Permissions API not supported');
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });

      status.onchange = () => {
        handlePermission(status.state);
      };

      handlePermission(status.state);
    } catch (err) {
      setError('Could not check geolocation permission.');
    }
  };

  const askForPermissionIfNeeded = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported.');
      return false;
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });

      if (status.state === 'granted') {
        await startWatching();
        return true;
      }

      if (status.state === 'denied') {
        setError('Geolocation permission denied.');
        return false;
      }

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setCoordinates({ latitude, longitude });
            setIsEnabled(true);
            setError(null);
            startWatching();
            resolve(true);
          },
          (err) => {
            setError(err.message);
            setIsEnabled(false);
            resolve(false);
          },
          { enableHighAccuracy: true },
        );
      });
    } catch (err) {
      setError('Permission check failed.');
      return false;
    }
  };

  const requestPermission = () => {
    askForPermissionIfNeeded();
  };

  useEffect(() => {
    checkPermission();

    return () => {
      stopWatching();
    };
  }, []);

  return (
    <GeolocationContext.Provider
      value={{
        isSupported,
        isEnabled,
        coordinates,
        error,
        requestPermission,
        askForPermissionIfNeeded,
      }}
    >
      {children}
    </GeolocationContext.Provider>
  );
};

export default GeolocationContext